// Panel de la EXPANSIÓN MÁGICA — elegir el formato destino y ver el encuadre
// nuevo ANTES de gastar el crédito.
//
// Presentacional a propósito: la llamada al backend, el chequeo de saldo y qué
// hacer con la imagen resultante los maneja cada superficie (el inline de recién
// generadas y el historial), que ya tienen su propio estado y sus modales de
// límite. Acá vive lo único que es igual en las dos: el preview y la elección.
//
// El esqueleto (panel derecho en desktop, sheet arrastrable en mobile) es el
// mismo de "Mejorar resolución" — la herramienta se suma al menú de al lado, así
// que salirse del molde se notaría como un parche.
// Ver ADR-2026-08-17-expansion-magica.

import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Info, Loader2, X } from "lucide-react";
import {
  CREDITOS_EXPANSION,
  encuadreOriginal,
  ladoQueCrece,
  opcionesParaImagen,
  type RatioExpansion,
} from "../lib/expansionMagica";

interface ExpansionMagicaSheetProps {
  abierto: boolean;
  /** URL (firmada) de la imagen a expandir. */
  imagenUrl: string;
  procesando: boolean;
  onCerrar: () => void;
  onExpandir: (ratio: RatioExpansion) => void;
}

export default function ExpansionMagicaSheet({
  abierto,
  imagenUrl,
  procesando,
  onCerrar,
  onExpandir,
}: ExpansionMagicaSheetProps) {
  const [animado, setAnimado] = useState(false);
  const [dragY, setDragY] = useState(0);
  const dragStartY = useRef<number | null>(null);
  // Medidas REALES de la imagen: definen qué formatos agregan lienzo y cuánto
  // ocupa la original dentro del marco destino.
  const [medidas, setMedidas] = useState<{ w: number; h: number }>({ w: 0, h: 0 });
  const [errorMedidas, setErrorMedidas] = useState(false);
  const [ratio, setRatio] = useState<RatioExpansion | null>(null);

  const opciones = opcionesParaImagen(medidas.w, medidas.h);

  useEffect(() => {
    // Al cerrar (incluso si lo cierra el padre tras expandir) se resetea la
    // animación: si no, la próxima apertura aparece de golpe, ya "entrada".
    if (!abierto) {
      setAnimado(false);
      return;
    }
    setDragY(0);
    setMedidas({ w: 0, h: 0 });
    setErrorMedidas(false);
    setRatio(null);
    const id = requestAnimationFrame(() => setAnimado(true));
    return () => cancelAnimationFrame(id);
  }, [abierto]);

  // Las medidas se sacan de un preloader y NO del `onLoad` del <img> del
  // preview: la imagen que se expande es la que el usuario está mirando, o sea
  // que ya está en la caché del browser y su `load` dispara antes de que React
  // enganche el handler. Con el evento perdido, `medidas` se quedaba en 0 y los
  // cuatro formatos aparecían deshabilitados para siempre ("Cargando la
  // imagen…"). `complete` cubre justamente ese caso. Sin `crossOrigin`, igual
  // que el <img> del preview, para no caer en otra partición de caché y
  // re-descargar (ADR-083).
  useEffect(() => {
    if (!abierto || !imagenUrl) return;
    let cancelado = false;
    const medir = (img: HTMLImageElement) => {
      if (!cancelado && img.naturalWidth > 0) {
        setMedidas({ w: img.naturalWidth, h: img.naturalHeight });
      }
    };
    const img = new Image();
    img.onload = () => medir(img);
    // Sin medidas no hay herramienta: se dice, en vez de dejar "Cargando…" para
    // siempre (una URL firmada vencida entraba justo por acá).
    img.onerror = () => {
      if (!cancelado) setErrorMedidas(true);
    };
    img.src = imagenUrl;
    if (img.complete) medir(img);
    return () => {
      cancelado = true;
      img.onload = null;
      img.onerror = null;
    };
  }, [abierto, imagenUrl]);

  // Preselección: el primer formato que realmente expande. Se hace cuando la
  // imagen carga (antes no sabemos cuál es el encuadre actual).
  useEffect(() => {
    if (ratio !== null) return;
    const primera = opciones.find((o) => o.disponible);
    if (primera) setRatio(primera.id);
  }, [opciones, ratio]);

  const cerrar = () => {
    setAnimado(false);
    setTimeout(() => {
      setDragY(0);
      onCerrar();
    }, 300);
  };

  const marco = ratio
    ? opciones.find((o) => o.id === ratio)!
    : { w: 4, h: 5, id: "4:5" as RatioExpansion };
  const { anchoPct, altoPct } = ratio
    ? encuadreOriginal(ratio, medidas.w, medidas.h)
    : { anchoPct: 1, altoPct: 1 };
  const crece = ratio ? ladoQueCrece(ratio, medidas.w, medidas.h) : null;
  const listo = ratio !== null && medidas.w > 0 && !procesando;

  const preview = (
    <div className="flex justify-center">
      <div
        className="relative w-full max-w-[260px] overflow-hidden rounded-[10px] bg-neutral-100"
        style={{
          // `aspect-[w/h]` de Tailwind no compila en este proyecto: va inline.
          aspectRatio: `${marco.w} / ${marco.h}`,
          transition: "aspect-ratio 300ms ease-out",
        }}
      >
        {/* Fondo: la misma imagen estirada y borroneada. Es un placeholder de lo
            que la IA va a completar — no promete un resultado, da el volumen.
            Mientras se expande, LATE: el movimiento está donde está el trabajo. */}
        <img
          src={imagenUrl}
          alt=""
          aria-hidden
          className={`absolute inset-0 w-full h-full object-cover blur-xl ${procesando ? "expansion-latido" : "opacity-80"}`}
          style={{ transform: "scale(1.25)" }}
        />
        {/* Original nítida, del tamaño EXACTO que va a ocupar en el encuadre nuevo. */}
        <img
          src={imagenUrl}
          alt="Vista previa de la expansión"
          className="absolute left-1/2 top-1/2 object-contain"
          style={{
            width: `${anchoPct * 100}%`,
            height: `${altoPct * 100}%`,
            transform: "translate(-50%, -50%)",
            transition: "width 300ms ease-out, height 300ms ease-out",
            boxShadow: "0 0 0 1px rgba(255,255,255,0.65)",
          }}
        />
        {/* Brillo que barre el marco mientras corre la generación. */}
        {procesando && (
          <div
            aria-hidden
            className="absolute inset-y-0 -left-1/3 w-1/3 expansion-barrido pointer-events-none"
            style={{
              background:
                "linear-gradient(90deg, rgba(255,255,255,0) 0%, rgba(255,255,255,0.45) 50%, rgba(255,255,255,0) 100%)",
            }}
          />
        )}
      </div>
    </div>
  );

  // Estado "expandiendo": barra de progreso con el mismo lenguaje que el resto
  // de las esperas largas de la app (gris → primario, sin porcentaje falso).
  const progreso = procesando ? (
    <div className="mt-4" role="status" aria-live="polite">
      <div className="flex items-center gap-2 mb-2">
        <Loader2 className="w-4 h-4 animate-spin text-brand-primary" />
        <span className="text-[13px] text-brand-black/80">
          Completando la escena…
        </span>
      </div>
      <div className="h-1.5 rounded-full overflow-hidden bg-neutral-200">
        <div
          className="h-full rounded-full"
          style={{
            width: "100%",
            transformOrigin: "left",
            // Arranca en 0 y tarda ~40s en llegar al final: la expansión suele
            // resolverse antes, y una barra que se planta en 100% mintiendo es
            // peor que una que todavía va por la mitad.
            animation: "expansion-progreso 40s cubic-bezier(0, 0, 0.2, 1) forwards",
            background:
              "linear-gradient(to right, #D1D5DB 10%, var(--brand-primary) 70%)",
          }}
        />
      </div>
    </div>
  ) : null;

  const selectorFormatos = (
    <div>
      <p className="text-[13px] font-normal text-[#3C3C43]/60 uppercase tracking-wide mb-2">
        Formato
      </p>
      <div className="grid grid-cols-4 gap-2">
        {opciones.map((op) => {
          const activo = op.id === ratio;
          return (
            <button
              key={op.id}
              type="button"
              disabled={!op.disponible || procesando}
              onClick={() => setRatio(op.id)}
              title={
                op.disponible
                  ? op.etiqueta
                  : medidas.w === 0
                    ? "Todavía estamos leyendo la imagen"
                    : "La imagen ya tiene este formato"
              }
              className={`flex flex-col items-center justify-center gap-1.5 py-3 rounded-lg border transition-colors ${
                activo
                  ? "border-[#1644EB] bg-[#EEF2FE]"
                  : "border-neutral-200 bg-white hover:bg-neutral-50"
              } ${!op.disponible || procesando ? "opacity-40 cursor-not-allowed hover:bg-white" : ""}`}
            >
              {/* Miniatura del encuadre: 22px de lado mayor. */}
              <span
                className={`block rounded-[3px] border-2 ${activo ? "border-[#1644EB]" : "border-neutral-400"}`}
                style={{
                  width: op.w >= op.h ? 22 : (22 * op.w) / op.h,
                  height: op.h >= op.w ? 22 : (22 * op.h) / op.w,
                }}
              />
              <span
                className={`text-[11px] font-medium ${activo ? "text-[#1644EB]" : "text-brand-black/70"}`}
              >
                {op.id}
              </span>
            </button>
          );
        })}
      </div>
      <p className="text-[13px] text-[#3C3C43]/60 mt-2">
        {errorMedidas
          ? "No pudimos leer la imagen. Cerrá y volvé a abrirla."
          : medidas.w === 0
            ? "Cargando la imagen…"
            : crece
            ? `La IA completa ${crece === "costados" ? "los costados" : "arriba y abajo"} sin recortar nada de la foto.`
            : "Elegí un formato distinto al actual."}
      </p>
    </div>
  );

  const info = (
    <div className="mt-4">
      <p className="text-[13px] font-normal text-[#3C3C43]/60 uppercase tracking-wide mb-2">
        Información
      </p>
      <div className="rounded-[13px] bg-white overflow-hidden border border-neutral-200">
        <div className="px-4 py-[11px]">
          <div className="flex items-center gap-3 mb-2">
            <span className="material-symbols-rounded text-[#1644EB] text-[20px] shrink-0">
              expand_content
            </span>
            <span className="text-[15px] text-brand-black/90">
              Se guarda como imagen nueva
            </span>
          </div>
          <p className="text-[13px] text-[#3C3C43]/60">
            La original queda intacta en tu historial: la expandida se suma como una
            imagen aparte.
          </p>
        </div>
        <div className="ml-[46px] h-px bg-black/[0.08]" />
        <div className="px-4 py-[11px]">
          <div className="flex items-center gap-2">
            <Info className="w-3.5 h-3.5 text-[#1644EB] shrink-0" />
            <span className="text-[13px] text-[#3C3C43]/60">
              {CREDITOS_EXPANSION === 1
                ? "Se consumirá 1 imagen"
                : `Se consumirán ${CREDITOS_EXPANSION} imágenes`}
            </span>
          </div>
        </div>
      </div>
    </div>
  );

  if (!abierto) return null;

  return createPortal(
    <>
      {/* Backdrop */}
      <div
        className={`fixed inset-0 z-[200] bg-black/40 transition-opacity duration-300 ${animado ? "opacity-100" : "opacity-0"}`}
        onClick={procesando ? undefined : cerrar}
      />

      {/* Desktop: panel lateral */}
      <div
        className={`hidden sm:flex fixed top-4 right-4 bottom-4 w-[420px] bg-white rounded-[13px] shadow-[0_8px_40px_rgba(0,0,0,0.12),0_0_0_0.5px_rgba(0,0,0,0.06)] z-[201] flex-col overflow-hidden transition-transform duration-300 ease-out ${animado ? "translate-x-0" : "translate-x-[calc(100%+32px)]"}`}
      >
        <div className="flex items-center justify-between px-5 py-4">
          <h3 className="text-[17px] font-semibold text-brand-black/90">
            Expansión mágica
          </h3>
          <button
            type="button"
            onClick={cerrar}
            disabled={procesando}
            className="w-8 h-8 inline-flex items-center justify-center rounded-full bg-black/[0.06] text-brand-black/40 hover:bg-black/[0.1] transition-colors disabled:opacity-50"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="mx-5 h-px bg-black/[0.08]" />
        <div className="flex-1 overflow-y-auto px-5 py-4">
          <div className="mb-6">
            {preview}
            {progreso}
          </div>
          {selectorFormatos}
          {info}
        </div>
        <div className="px-5 py-3 flex justify-end gap-3">
          <button
            type="button"
            onClick={cerrar}
            disabled={procesando}
            className="text-[15px] text-[#3C3C43]/60 hover:text-brand-black/80 px-3 py-2 transition-colors disabled:opacity-50"
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={() => ratio && onExpandir(ratio)}
            disabled={!listo}
            className="inline-flex items-center gap-2 text-[15px] font-semibold text-white bg-brand-primary hover:bg-brand-primary-hover rounded-md px-4 py-2 shadow-sm transition-colors disabled:opacity-50"
          >
            {procesando && <Loader2 className="w-4 h-4 animate-spin" />}
            {procesando ? "Expandiendo..." : "Expandir"}
          </button>
        </div>
      </div>

      {/* Mobile: sheet de alto completo */}
      <div
        className="sm:hidden fixed inset-x-0 bottom-0 z-[201] bg-white rounded-t-[13px] flex flex-col"
        style={{
          height: "calc(100dvh - 1rem - env(safe-area-inset-top, 0px))",
          paddingBottom: "calc(env(safe-area-inset-bottom, 0px) + 16px)",
          transform:
            dragY > 0
              ? `translateY(${dragY}px)`
              : animado
                ? "translateY(0)"
                : "translateY(100%)",
          transition: dragY > 0 ? "none" : "transform 300ms ease-out",
        }}
      >
        <div
          className="py-2 cursor-grab active:cursor-grabbing shrink-0"
          onTouchStart={(e) => {
            if (procesando) return;
            dragStartY.current = e.touches[0].clientY;
          }}
          onTouchMove={(e) => {
            if (dragStartY.current !== null) {
              const dy = e.touches[0].clientY - dragStartY.current;
              if (dy > 0) setDragY(dy);
            }
          }}
          onTouchEnd={() => {
            dragStartY.current = null;
            if (dragY > 100) cerrar();
            else setDragY(0);
          }}
        >
          <div className="w-9 h-[5px] rounded-full bg-black/[0.15] mx-auto" />
        </div>
        {/* Nav: Cancelar · título. La acción primaria NO va acá: es un botón
            ancho abajo, que en mano es donde cae el pulgar. El `w-20` de la
            derecha es el contrapeso que mantiene el título centrado. */}
        <div className="flex items-center justify-between px-4 pb-3 shrink-0">
          <button
            type="button"
            onClick={cerrar}
            disabled={procesando}
            className="text-[15px] text-[#1644EB] active:opacity-50 transition-opacity w-20 text-left disabled:opacity-50"
          >
            Cancelar
          </button>
          <h3 className="text-[15px] font-semibold text-brand-black/90">
            Expansión mágica
          </h3>
          <span className="w-20" aria-hidden />
        </div>
        <div className="flex-1 overflow-y-auto px-4 pb-4 pt-6">
          <div className="mb-6">
            {preview}
            {progreso}
          </div>
          {selectorFormatos}
          {info}
        </div>
        {/* CTA ancho, pegado abajo y siempre visible (fuera del scroll). */}
        <div className="px-4 pt-3 shrink-0 border-t border-black/[0.06]">
          <button
            type="button"
            onClick={() => ratio && onExpandir(ratio)}
            disabled={!listo}
            className="w-full inline-flex items-center justify-center gap-2 py-3 px-6 rounded-2xl bg-brand-primary hover:bg-brand-primary-hover text-white text-[15px] font-semibold shadow-sm transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {procesando && <Loader2 className="w-4 h-4 animate-spin" />}
            {procesando ? "Expandiendo…" : "Expandir"}
          </button>
        </div>
      </div>
    </>,
    document.body,
  );
}
